import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import './Opportunities.css';
import BlueskyPostCard from './BlueskyPostCard';
import NotificationsPill from './NotificationsPill';
import SpotlightTab from './SpotlightTab';
import DramaturgWorkshop from './DramaturgWorkshop';
import HealthTab from './HealthTab';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Helper to build fetch options with auth
const buildFetchOptions = (options = {}) => {
  const adminToken = localStorage.getItem('admin_jwt');
  const headers = options.headers || {};
  
  // Add Authorization header if token exists (backward compatibility)
  if (adminToken && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${adminToken}`;
  }
  
  return {
    ...options,
    headers,
    credentials: 'include'  // Always send cookies
  };
};

const CHARACTER_KEYS = {
  'lady macbeth': ['lady macbeth'],
  'falstaff': ['falstaff'],
  'miranda': ['miranda'],
  'iago': ['iago'],
  'sir andrew aguecheek': ['sir andrew', 'sir andrew aguecheek'],
  'sir andrew': ['sir andrew', 'sir andrew aguecheek'],
};

const Opportunities = () => {
  const [opportunities, setOpportunities] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(1800); // 30 minutes = 1800 seconds
  const [showLogin, setShowLogin] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState(null);
  const [activeTab, setActiveTab] = useState('reply'); // reply | spotlight | health | dramaturg
  const [poolSummary, setPoolSummary] = useState({ pending_demotions: 0, mining_candidates: 0 });
  const [charAvatars, setCharAvatars] = useState({});
  const [toast, setToast] = useState(null); // Auto-dismissing notifications
  const [externalCandidates, setExternalCandidates] = useState([]);
  const [extPostsRemaining, setExtPostsRemaining] = useState(null);
  const [threadPosts, setThreadPosts] = useState({}); // URI -> full post object
  const [charOverrideOpen, setCharOverrideOpen] = useState({}); // opportunity_id -> bool
  const [isMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches);
  const [removingOpps, setRemovingOpps] = useState({}); // opp_id -> 'left' | 'right' (animation direction)
  const [removingQuotes, setRemovingQuotes] = useState({}); // cardKey -> 'left'
  const [removingExternal, setRemovingExternal] = useState({}); // candidate.id -> 'left'
  const [batchLocked, setBatchLocked] = useState(false);
  const [proposedPosts, setProposedPosts] = useState([]);
  const [quotePacerMeta, setQuotePacerMeta] = useState(null);
  const [sqPackages, setSqPackages] = useState([]);
  const [sqStats, setSqStats] = useState(null);
  const [sqActionId, setSqActionId] = useState(null);
  const [removingSq, setRemovingSq] = useState({});
  const [quoteActionKey, setQuoteActionKey] = useState(null);
  const [quoteCharOverrides, setQuoteCharOverrides] = useState({}); // cardKey -> character string
  const removedOppsRef = useRef(new Map()); // opp_id -> { item, index }
  const removalTimersRef = useRef(new Map()); // opp_id -> timeout id
  const actedPostUrlsRef = useRef(new Set()); // post_urls we've posted/skipped — filters stale cache dupes
  const opportunitiesRef = useRef([]);
  const fetchTokenRef = useRef(0);
  const batchStartSizeRef = useRef(0); // Track original batch size to prevent premature unlock
  const batchLockedRef = useRef(false); // Sync ref for immediate checks (React state is async)
  const errorRetryRef = useRef(null); // PROD-007: auto-retry timer on network/server errors
  const prefetchedBatchRef = useRef(null); // Pre-fetched next batch (loaded when current batch drops to <=2)
  const oppsListRef = useRef(null); // DOM ref for scroll-position preservation
  const prevOppsTopRef = useRef(null); // track reply-list offsetTop for scroll compensation

  // Check for ?logout=1 parameter to force credential reset (useful on mobile)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('logout') === '1') {
      // Clear all auth state
      localStorage.removeItem('admin_jwt');
      document.cookie = 'admin_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      
      // Remove the parameter from URL without reload
      params.delete('logout');
      const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '') + window.location.hash;
      window.history.replaceState({}, '', newUrl);
      
      // Force login screen
      setShowLogin(true);
      setLoading(false);
    }
  }, []);

  const removeOpportunityLocally = (oppId, direction = null, delayMs = 0) => {
    if (direction) {
      setRemovingOpps(prev => ({ ...prev, [oppId]: direction }));
    }

    const doRemove = () => {
      setOpportunities(prev => {
        const index = prev.findIndex(o => o.id === oppId);
        if (index === -1) {
          return prev;
        }
        const item = prev[index];
        removedOppsRef.current.set(oppId, { item, index });
        if (item.post_url) actedPostUrlsRef.current.add(item.post_url);
        return prev.filter(o => o.id !== oppId);
      });
      // Also filter from prefetched batch so it doesn't reappear on batch-clear
      if (prefetchedBatchRef.current?.opportunities) {
        prefetchedBatchRef.current.opportunities =
          prefetchedBatchRef.current.opportunities.filter(o => o.id !== oppId);
      }
      if (removalTimersRef.current.has(oppId)) {
        removalTimersRef.current.delete(oppId);
      }
      if (direction) {
        setRemovingOpps(prev => {
          const copy = { ...prev };
          delete copy[oppId];
          return copy;
        });
      }
    };

    if (delayMs > 0) {
      const timerId = setTimeout(doRemove, delayMs);
      removalTimersRef.current.set(oppId, timerId);
    } else {
      doRemove();
    }
  };

  const restoreOpportunityLocally = (oppId) => {
    const pendingTimer = removalTimersRef.current.get(oppId);
    if (pendingTimer) {
      clearTimeout(pendingTimer);
      removalTimersRef.current.delete(oppId);
      return;
    }
    const cached = removedOppsRef.current.get(oppId);
    if (!cached) return;
    removedOppsRef.current.delete(oppId);
    setOpportunities(prev => {
      if (prev.some(o => o.id === oppId)) return prev;
      const next = [...prev];
      const insertAt = Math.min(cached.index, next.length);
      next.splice(insertAt, 0, cached.item);
      return next;
    });
  };

  const prefetchInFlightRef = useRef(false);

  const prefetchNextBatch = async () => {
    if (prefetchedBatchRef.current || prefetchInFlightRef.current) return;
    prefetchInFlightRef.current = true;
    console.log('[PREFETCH] Starting background fetch');
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/opportunities`, buildFetchOptions());
      if (!response.ok) return;
      const data = await response.json();
      const opps = data.opportunities || [];
      if (opps.length > 0) {
        prefetchedBatchRef.current = data;
        console.log('[PREFETCH] Cached', opps.length, 'opportunities');
      }
    } catch (err) {
      console.warn('[PREFETCH] Failed (non-fatal):', err.message);
    } finally {
      prefetchInFlightRef.current = false;
    }
  };

  useEffect(() => {
    opportunitiesRef.current = opportunities;
    if (opportunities.length === 0 && batchStartSizeRef.current > 0) {
      console.log('[BATCH] All items cleared, unlocking batch');
      setBatchLocked(false);
      batchLockedRef.current = false;
      batchStartSizeRef.current = 0;
      // Apply prefetched data immediately if available (no network call)
      if (prefetchedBatchRef.current) {
        const cached = prefetchedBatchRef.current;
        prefetchedBatchRef.current = null;
        console.log('[PREFETCH] Applying cached batch on clear');
        // Defer to next tick so React state from above has settled
        setTimeout(() => applyBatchData(cached), 0);
      }
      // Otherwise the 1s countdown timer will trigger fetchOpportunities()
    }
    // Pre-fetch next batch when running low
    if (opportunities.length > 0 && opportunities.length <= 2 && batchLockedRef.current) {
      prefetchNextBatch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunities]);

  // Detect mobile on resize

  const fetchThreadPosts = async (uris) => {
    if (!uris || uris.length === 0) return;

    try {
      const adminToken = localStorage.getItem('admin_jwt');
      const response = await fetch(`${BACKEND_URL}/api/admin/opportunities/fetch-thread-posts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ uris })
      });

      if (response.ok) {
        const data = await response.json();
        setThreadPosts(prev => ({ ...prev, ...data.posts }));
      }
    } catch (err) {
      console.error('Error fetching thread posts:', err);
    }
  };

  // Check if session (cookie) is still valid and sync to localStorage if needed
  const checkAndSyncSession = async () => {
    try {
      const sessionCheck = await fetch(`${BACKEND_URL}/api/admin/session-check`, {
        credentials: 'include'
      });
      
      if (sessionCheck.ok) {
        const sessionData = await sessionCheck.json();
        if (sessionData.valid && sessionData.token) {
          // Cookie is valid, sync token to localStorage
          localStorage.setItem('admin_jwt', sessionData.token);
          return true;
        }
      }
      return false;
    } catch (e) {
      console.warn('Session check failed:', {
        error: e.name,
        message: e.message,
        backendUrl: BACKEND_URL
      });
      return false;
    }
  };

  const applyBatchData = (data) => {
    const raw = data.opportunities || [];
    const opps = raw.filter(o =>
      !actedPostUrlsRef.current.has(o.post_url) && !o.training_review
    );
    if (opps.length < raw.length) {
      console.log(
        `[DEDUP] Filtered ${raw.length - opps.length} already-acted/training rows from batch`
      );
    }
    setOpportunities(opps);
    setProposedPosts(data.proposed_posts || []);
    setQuotePacerMeta(data.filter_stats?.quote_pacer ?? null);
    setMetadata({
      lastScan: data.last_scan_mt,
      nextScan: data.next_scan_in_minutes,
      scanInterval: data.scan_interval_minutes || 5,
      windowHours: data.opportunity_window_hours || 6,
      currentTime: data.current_time_mt,
      batchComposition: data.batch_composition || {},
      sourceCounts: data.source_counts || {},
      newTargetCount: data.new_target_count || 0,
      filterStats: data.filter_stats || {}
    });
    if (opps.length > 0) {
      setBatchLocked(true);
      batchLockedRef.current = true;
      batchStartSizeRef.current = opps.length;
      console.log('[BATCH] New batch locked:', opps.length, 'items');
    }
    const threadUris = [];
    opps.forEach(opp => {
      if (opp.parent_uri) threadUris.push(opp.parent_uri);
      if (opp.root_uri && opp.root_uri !== opp.parent_uri) threadUris.push(opp.root_uri);
    });
    if (threadUris.length > 0) {
      fetchThreadPosts([...new Set(threadUris)]);
    }
    setLoading(false);
    setCountdown(1800);
    // After new batch renders, scroll to bottom so user can work bottom-up
    requestAnimationFrame(() => {
      if (oppsListRef.current) {
        prevOppsTopRef.current = oppsListRef.current.offsetTop;
        const list = oppsListRef.current;
        const lastChild = list.lastElementChild;
        if (lastChild) {
          lastChild.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    });
  };

  const fetchOpportunities = async ({ force = false, refreshQuotesOnly = false } = {}) => {
    try {
      // Do not replace an active batch unless forced or empty (quote strip can still refresh)
      if (!refreshQuotesOnly && !force && batchLocked && opportunitiesRef.current.length > 0) {
        return;
      }

      const fetchToken = ++fetchTokenRef.current;
      if (!refreshQuotesOnly) {
        setLoading(true);
        setError(null);
      }
      if (errorRetryRef.current?.timer) clearTimeout(errorRetryRef.current.timer);
      errorRetryRef.current = null;
      
      // Skip session-check when JWT already exists — the 401 handler below
      // will trigger re-auth if the token is actually expired.
      const hasJwt = !!localStorage.getItem('admin_jwt');
      const hasValidSession = (refreshQuotesOnly || hasJwt) ? true : await checkAndSyncSession();
      
      let response;
      try {
        response = await fetch(`${BACKEND_URL}/api/admin/opportunities`, buildFetchOptions());
      } catch (fetchErr) {
        // Network error - fetch itself failed
        console.error('Fetch error:', fetchErr);
        if (fetchErr.name === 'TypeError' && fetchErr.message.includes('fetch')) {
          throw new Error(`Network error: Cannot reach ${BACKEND_URL}. Server may be down or unreachable.`);
        } else if (fetchErr.name === 'AbortError') {
          throw new Error('Request timed out. Server may be slow or unreachable.');
        }
        throw fetchErr;
      }

      if (response.status === 401 || response.status === 403) {
        // If we just synced session but still got 401, cookie is also expired
        if (!hasValidSession) {
          localStorage.removeItem('admin_jwt');
          setShowLogin(true);
          setLoading(false);
          return;
        }
        // Retry once after session sync (cookie might be valid but localStorage was stale)
        response = await fetch(`${BACKEND_URL}/api/admin/opportunities`, buildFetchOptions());
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('admin_jwt');
          setShowLogin(true);
          setLoading(false);
          return;
        }
      }

      if (!response.ok) {
        // 502 Bad Gateway - backend not running
        if (response.status === 502) {
          throw new Error(`Server unavailable (502): Backend application is not responding. The server may be down or restarting.`);
        }
        // Any auth-like error should trigger login, not generic error
        if (response.status >= 400 && response.status < 500) {
          localStorage.removeItem('admin_jwt');
          setShowLogin(true);
          setLoading(false);
          return;
        }
        // Server errors (5xx) - provide more context
        const errorText = await response.text().catch(() => response.statusText);
        throw new Error(`Server error (HTTP ${response.status}): ${errorText || response.statusText}`);
      }

      const data = await response.json();

      // Ignore stale in-flight responses
      if (fetchToken !== fetchTokenRef.current) {
        return;
      }

      if (refreshQuotesOnly) {
        setProposedPosts(data.proposed_posts || []);
        setQuotePacerMeta(data.filter_stats?.quote_pacer ?? null);
        setLoading(false);
        return;
      }

      const opps = data.opportunities || [];
      const hasActiveBatch = batchLockedRef.current && batchStartSizeRef.current > 0;
      const hasItems = opportunitiesRef.current.length > 0;
      const shouldApply = force || (!hasActiveBatch && !hasItems);

      console.log('[BATCH] Fetch complete:', { 
        force, hasActiveBatch, hasItems, shouldApply, 
        batchLocked: batchLockedRef.current, 
        batchSize: batchStartSizeRef.current,
        currentCount: opportunitiesRef.current.length,
        newCount: opps.length 
      });

      if (shouldApply) {
        prefetchedBatchRef.current = null;
        applyBatchData(data);
      } else {
        console.log('[BATCH] Fetch ignored - batch is active');
        setLoading(false);
      }
      
    } catch (err) {
      console.error('Error fetching opportunities:', err);
      
      // Provide specific error messages based on error type
      let errorMessage = 'Failed to load opportunities';
      let isRetryable = false;
      
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        errorMessage = `Network error: Cannot reach server. Retrying...`;
        isRetryable = true;
      } else if (err.name === 'AbortError') {
        errorMessage = 'Request timed out. Retrying...';
        isRetryable = true;
      } else if (err.message && (err.message.includes('502') || err.message.includes('503') || err.message.includes('unavailable'))) {
        errorMessage = `Server restarting. Retrying...`;
        isRetryable = true;
      } else if (err.message) {
        errorMessage = `Error: ${err.message}`;
      }
      
      // Add diagnostic info
      console.error('Error details:', {
        name: err.name,
        message: err.message,
        stack: err.stack,
        backendUrl: BACKEND_URL
      });
      
      setError(errorMessage);
      setLoading(false);

      // PROD-007: Auto-retry with backoff on network/server errors.
      // Backend restarts take ~18s; retry at 5s, 10s, 30s to recover quickly.
      if (isRetryable) {
        const attempt = (errorRetryRef.current?.attempt || 0) + 1;
        const delays = [5000, 10000, 30000];
        const delay = delays[Math.min(attempt - 1, delays.length - 1)];
        if (attempt <= 5) {
          console.log(`[RETRY] Attempt ${attempt} in ${delay / 1000}s`);
          if (errorRetryRef.current?.timer) clearTimeout(errorRetryRef.current.timer);
          const timer = setTimeout(() => fetchOpportunities({ force: true }), delay);
          errorRetryRef.current = { timer, attempt };
        } else {
          console.log('[RETRY] Max retries reached, waiting for manual refresh');
          setError(errorMessage.replace('Retrying...', 'Please retry manually.'));
          errorRetryRef.current = null;
        }
      } else {
        errorRetryRef.current = null;
      }
    }
  };

  // Load character avatars for our troupe (used on Replies tab)
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/ui/chat/avatars`);
        if (r.ok) {
          const data = await r.json();
          setCharAvatars(data || {});
        }
      } catch {
        // Non-fatal for Opportunities if avatars endpoint fails
      }
    })();
  }, []);

  useEffect(() => {
    fetchOpportunities({ force: true });
    fetchSqPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Migrate old Funnel/Pool deep links → Health
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab === 'funnel' || tab === 'pool' || tab === 'health') {
      setActiveTab('health');
      if (tab !== 'health') {
        params.set('tab', 'health');
        const newUrl = window.location.pathname + '?' + params.toString() + window.location.hash;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, []);

  // Lightweight pool-summary poll for the Health tab badge.
  useEffect(() => {
    if (showLogin) return undefined;
    let cancelled = false;
    const fetchPool = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/admin/pool/summary`, buildFetchOptions());
        if (!r.ok) return;
        const json = await r.json();
        if (!cancelled) setPoolSummary(json || {});
      } catch (_) { /* silent */ }
    };
    fetchPool();
    const id = setInterval(fetchPool, 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [showLogin]);

  // Auto-refresh only when current batch is fully cleared (user processed all items)
  // This keeps the batch stable while working, then auto-fetches next batch
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          // Only auto-refresh if batch is completely empty AND unlocked
          // Use refs for immediate values - React state can be stale in interval callbacks
          const isEmpty = opportunitiesRef.current.length === 0;
          const isUnlocked = !batchLockedRef.current;
          if (isEmpty && isUnlocked) {
            console.log('[BATCH] Auto-refresh: batch empty and unlocked');
            fetchOpportunities({ force: true });
          }
          return 1800; // Reset timer regardless
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // No dependencies - use refs for current values

  // Scroll compensation: when quote-post or external-content sections above
  // the reply list grow/shrink, the reply list shifts down/up. Compensate
  // window.scrollY so the user's viewport stays pinned to the same reply card
  // they were reading (bottom-up workflow).
  useLayoutEffect(() => {
    const el = oppsListRef.current;
    if (!el) return;
    const newTop = el.offsetTop;
    if (prevOppsTopRef.current != null && newTop !== prevOppsTopRef.current) {
      const delta = newTop - prevOppsTopRef.current;
      window.scrollBy(0, delta);
    }
    prevOppsTopRef.current = newTop;
  }, [proposedPosts, externalCandidates, sqPackages]);

  // Auto-refresh quote-post candidates every 90s so all devices stay in sync
  useEffect(() => {
    if (activeTab !== 'reply' || showLogin) return;
    const interval = setInterval(() => {
      fetchOpportunities({ refreshQuotesOnly: true });
    }, 90 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, showLogin]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
        credentials: 'include'  // Receive and store cookies
      });

      if (!response.ok) {
        // Provide specific error based on status code
        let errorMsg = 'Invalid password';
        if (response.status === 502) {
          errorMsg = 'Server unavailable (502): Backend application is not responding. The server may be down or restarting.';
        } else if (response.status === 503) {
          errorMsg = 'Server error: Authentication not configured';
        } else if (response.status === 401) {
          errorMsg = 'Invalid password';
        } else if (response.status >= 500) {
          errorMsg = `Server error (${response.status}). Please try again later.`;
        } else {
          errorMsg = `Login failed: HTTP ${response.status}`;
        }
        setLoginError(errorMsg);
        return;
      }

      const data = await response.json();
      // Still store in localStorage for backward compatibility
      // But cookie is now the primary auth method
      localStorage.setItem('admin_jwt', data.token);
      setShowLogin(false);
      setPassword('');
      fetchOpportunities({ force: true });
      fetchSqPackages();
      
    } catch (err) {
      console.error('Login error details:', {
        name: err.name,
        message: err.message,
        stack: err.stack,
        backendUrl: BACKEND_URL
      });
      
      // Provide specific error messages
      let errorMsg = 'Login failed';
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        errorMsg = `Network error: Cannot reach ${BACKEND_URL}. Check server status and CORS configuration.`;
      } else if (err.name === 'AbortError') {
        errorMsg = 'Request timed out. Server may be slow or unreachable.';
      } else if (err.message) {
        errorMsg = `Login failed: ${err.message}`;
      }
      
      setLoginError(errorMsg);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const copyReplyCommand = (character, postUrl, oppId) => {
    // Map character names to Discord command shortcuts
    const charMap = {
      'Lady Macbeth': 'LM',
      'Falstaff': 'F',
      'Miranda': 'M',
      'Iago': 'I',
      'Sir Andrew Aguecheek': 'Andrew',
      'Sir Andrew': 'Andrew'
    };
    
    const charToken = charMap[character] || character;
    const command = `!reply ${charToken} ${postUrl}`;
    navigator.clipboard.writeText(command).then(() => {
      alert(`Copied to clipboard:\n${command}\n\nPaste in Discord admin channel!`);
      // Dismiss-on-copy: once we've generated the command, treat this opportunity as handled.
      if (oppId) {
        skipOpportunity(oppId);
      }
    });
  };

  const skipOpportunity = async (oppId) => {
    try {
      const adminToken = localStorage.getItem('admin_jwt');
      
      // Optimistic removal - restore on failure to keep batch consistent
      removeOpportunityLocally(oppId);
      
      const response = await fetch(`${BACKEND_URL}/api/admin/opportunities/${oppId}/skip`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        const detail = errBody.detail || response.statusText || `HTTP ${response.status}`;
        console.error('Error skipping opportunity:', oppId, response.status, detail);
        restoreOpportunityLocally(oppId);
        showToast(`Skip failed: ${detail} (restored)`, 3000);
      }
    } catch (err) {
      console.error('Error skipping:', err);
      restoreOpportunityLocally(oppId);
      showToast(`Skip failed: ${err.message || 'Network error'} (restored)`, 3000);
    }
  };

  const skipOpportunityWithReason = async (oppId, reason) => {
    try {
      const adminToken = localStorage.getItem('admin_jwt');
      
      removeOpportunityLocally(oppId, 'left', 300);
      
      const response = await fetch(`${BACKEND_URL}/api/admin/skip-opportunity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          opportunity_id: oppId,
          skip_reason: reason
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        showToast(`Error: ${data.detail || 'Failed to skip'} (restored)`, 3000);
        restoreOpportunityLocally(oppId);
      }
    } catch (err) {
      console.error('Error skipping with reason:', err);
      showToast('Error: Failed to skip (restored)', 3000);
      restoreOpportunityLocally(oppId);
    }
  };


  // eslint-disable-next-line no-unused-vars
  const approveProspect = async (handle, tier = 2) => {
    try {
      const adminToken = localStorage.getItem('admin_jwt');
      const response = await fetch(`${BACKEND_URL}/api/admin/opportunities/prospects/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ handle, tier })
      });
      if (response.ok) {
        fetchOpportunities();
      }
    } catch (err) {
      alert(`Failed to approve ${handle}: ${err.message}`);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const dismissProspect = async (handle) => {
    try {
      const adminToken = localStorage.getItem('admin_jwt');
      const response = await fetch(`${BACKEND_URL}/api/admin/opportunities/prospects/dismiss`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ handle })
      });
      if (response.ok) {
        fetchOpportunities();
      }
    } catch (err) {
      alert(`Failed to dismiss ${handle}: ${err.message}`);
    }
  };

  const showToast = (message, duration = 1000) => {
    setToast(message);
    setTimeout(() => setToast(null), duration);
  };

  const fetchSqPackages = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/oe070/packages`, buildFetchOptions());
      if (!res.ok) return;
      const data = await res.json();
      setSqPackages(data.packages || []);
      setSqStats(data.stats || null);
    } catch {
      // Non-fatal; rail stays empty
    }
  };

  const decideSqPackage = async (pkg, verdict) => {
    if (sqActionId) return;
    setSqActionId(pkg.id);
    setRemovingSq((prev) => ({ ...prev, [pkg.id]: 'left' }));
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/oe070/packages/${pkg.id}/decide`,
        buildFetchOptions({
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ verdict }),
        })
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setRemovingSq((prev) => {
          const next = { ...prev };
          delete next[pkg.id];
          return next;
        });
        showToast(`SQ mark failed: ${err.detail || res.statusText}`, 3000);
      } else {
        setTimeout(() => {
          setSqPackages((prev) => prev.filter((p) => p.id !== pkg.id));
          setSqStats((prev) => {
            const next = { ...(prev || { pending: 0, approved: 0, vetoed: 0 }) };
            next.pending = Math.max(0, (next.pending || 1) - 1);
            next[verdict === 'approved' ? 'approved' : 'vetoed'] =
              (next[verdict === 'approved' ? 'approved' : 'vetoed'] || 0) + 1;
            return next;
          });
        }, 280);
      }
    } catch (err) {
      setRemovingSq((prev) => {
        const next = { ...prev };
        delete next[pkg.id];
        return next;
      });
      showToast(`SQ mark failed: ${err.message || 'network'}`, 3000);
    } finally {
      setSqActionId(null);
    }
  };

  const approveQuoteCandidate = async (c) => {
    const cardKey = `${c.pool}-${c.opp_id}-${c.character}`;
    if (quoteActionKey) return;
    setQuoteActionKey(cardKey);

    try {
      const characterOverride = quoteCharOverrides[cardKey];
      const body = {
        opp_id: c.opp_id,
        character: c.character,
        post_url: c.source_post_url,
        reply_uri: c.source_reply_uri || '',
        pool: c.pool,
      };
      if (characterOverride && characterOverride !== c.character) {
        body.character_override = characterOverride;
      }
      const response = await fetch(`${BACKEND_URL}/api/admin/proposed-posts/approve`, buildFetchOptions({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }));
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        const detail = errBody.detail || response.statusText;
        // Cadence / quality gates return clear messages — surface them.
        throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
      }
      // Remove only after success; refresh strip so weekly/24h caps apply immediately.
      setRemovingQuotes(prev => ({ ...prev, [cardKey]: 'left' }));
      setTimeout(() => {
        setProposedPosts(prev => prev.filter(p =>
          `${p.pool}-${p.opp_id}-${p.character}` !== cardKey
        ));
        setRemovingQuotes(prev => { const copy = { ...prev }; delete copy[cardKey]; return copy; });
      }, 350);
      setQuoteCharOverrides(prev => { const copy = { ...prev }; delete copy[cardKey]; return copy; });
      showToast('✓ Quote posted', 2500);
      fetchOpportunities({ refreshQuotesOnly: true });
    } catch (err) {
      console.error('Quote approve failed:', err);
      showToast(`❌ ${err.message}`, 5000);
    } finally {
      setQuoteActionKey(null);
    }
  };

  const dismissQuoteCandidate = async (c) => {
    const cardKey = `${c.pool}-${c.opp_id}-${c.character}`;

    setRemovingQuotes(prev => ({ ...prev, [cardKey]: 'left' }));

    setTimeout(() => {
      setProposedPosts(prev => prev.filter(p =>
        `${p.pool}-${p.opp_id}-${p.character}` !== cardKey
      ));
      setRemovingQuotes(prev => {
        const copy = { ...prev };
        delete copy[cardKey];
        return copy;
      });
    }, 350);

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/proposed-posts/reject`, buildFetchOptions({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reply_uri: c.source_reply_uri || null,
          post_url: c.source_post_url,
          character: c.character,
          pool: c.pool,
        }),
      }));
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        console.error('Quote dismiss backend error:', errBody.detail || response.statusText);
      }
    } catch (err) {
      console.error('Quote dismiss failed:', err);
    }
  };

  const postReplyToDiscord = async (character, postUrl, oppId) => {
    actedPostUrlsRef.current.add(postUrl);
    removeOpportunityLocally(oppId, 'right', 300);
    
    try {
      const adminToken = localStorage.getItem('admin_jwt');
      const response = await fetch(`${BACKEND_URL}/api/admin/post-reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          character, 
          post_url: postUrl,
          opportunity_id: oppId
        })
      });
      
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${response.status}`);
      }
      
    } catch (err) {
      restoreOpportunityLocally(oppId);
      showToast(`❌ Failed: ${err.message} (restored)`, 3000);
    }
  };

  // ── External Content Candidates (IE-022) ──

  const fetchExternalCandidates = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/external-content-candidates`,
        buildFetchOptions());
      if (!res.ok) return;
      const data = await res.json();
      setExternalCandidates(data.candidates || []);
      setExtPostsRemaining(data.posts_remaining ?? null);
    } catch (err) {
      console.error('[IE-022] fetch error:', err);
    }
  };

  // Fetch external candidates when reply tab is active
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeTab === 'reply' && !loading && !showLogin) fetchExternalCandidates(); }, [activeTab, loading, showLogin]);

  const approveExternalCandidate = async (candidate) => {
    const charOverride = charOverrideOpen[`ext-${candidate.id}`];
    // Optimistic removal: clear card immediately, post in background
    setRemovingExternal(prev => ({ ...prev, [candidate.id]: 'left' }));
    setTimeout(() => {
      setExternalCandidates(prev => prev.filter(c => c.id !== candidate.id));
      setRemovingExternal(prev => { const copy = { ...prev }; delete copy[candidate.id]; return copy; });
    }, 350);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/external-content/approve`,
        buildFetchOptions({
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: candidate.id,
            character_override: charOverride || null,
            x_auto_post: true,
          })
        }));
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${res.status}`);
      }
      setExtPostsRemaining(prev => prev != null ? Math.max(0, prev - 1) : prev);
    } catch (err) {
      showToast(`❌ ${err.message}`, 3000);
    }
  };

  const rejectExternalCandidate = async (candidate) => {
    setRemovingExternal(prev => ({ ...prev, [candidate.id]: 'left' }));

    setTimeout(() => {
      setExternalCandidates(prev => prev.filter(c => c.id !== candidate.id));
      setRemovingExternal(prev => {
        const copy = { ...prev };
        delete copy[candidate.id];
        return copy;
      });
    }, 350);

    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/external-content/reject`,
        buildFetchOptions({
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: candidate.id })
        }));
      if (!res.ok) {
        console.error('External content reject error:', res.statusText);
      }
    } catch (err) {
      console.error('External content reject failed:', err);
    }
  };

  if (showLogin) {
    return (
      <div className="opportunities-container">
        <h1>🔐 Admin Login</h1>
        <div className="login-container">
          <form onSubmit={handleLogin} className="login-form">
            <p>Enter admin password to access reply opportunities:</p>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              autoFocus
              className="password-input"
            />
            <button type="submit" className="btn-primary">
              Login
            </button>
            {loginError && (
              <div className="login-error">{loginError}</div>
            )}
          </form>
        </div>
      </div>
    );
  }

  if (loading && opportunities.length === 0) {
    return (
      <div className="opportunities-container">
        <h1>🎯 Reply Opportunities</h1>
        <div className="loading">Loading opportunities...</div>
      </div>
    );
  }

  if (error) {
    const isAutoRetrying = errorRetryRef.current?.attempt > 0;
    return (
      <div className="opportunities-container">
        <h1>🎯 Reply Opportunities</h1>
        <div className="error">
          <h2>⚠️ {error}</h2>
          {isAutoRetrying && (
            <p style={{ color: '#8ba', fontSize: '14px', marginTop: '8px' }}>
              Auto-retry {errorRetryRef.current.attempt}/5...
            </p>
          )}
          <button onClick={() => { errorRetryRef.current = null; fetchOpportunities({ force: true }); }} className="retry-btn">
            Retry Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="opportunities-container" data-mobile={isMobile ? 'true' : 'false'}>
      <h1>🎯 Opportunities Desk</h1>

      <div className="tab-bar">
        <button
          className={`tab-button ${activeTab === 'reply' ? 'active' : ''}`}
          onClick={() => setActiveTab('reply')}
        >
          Replies
        </button>
        <button
          className={`tab-button ${activeTab === 'spotlight' ? 'active' : ''}`}
          onClick={() => setActiveTab('spotlight')}
        >
          🔦 Spotlight
        </button>
        <button
          className={`tab-button ${activeTab === 'health' ? 'active' : ''}`}
          onClick={() => setActiveTab('health')}
          title="How replies are doing, and accounts to add or drop"
        >
          Health
          {(poolSummary.pending_demotions + poolSummary.mining_candidates) > 0 && (
            <span className="tab-badge">
              {poolSummary.pending_demotions + poolSummary.mining_candidates}
            </span>
          )}
        </button>
        <button
          className={`tab-button ${activeTab === 'dramaturg' ? 'active' : ''}`}
          onClick={() => setActiveTab('dramaturg')}
        >
          🎭 Dramaturg
        </button>
      </div>
      
      {activeTab === 'reply' && (
        <NotificationsPill 
          apiUrl={BACKEND_URL}
          adminToken={localStorage.getItem('admin_jwt')}
          onError={(err) => setToast(err)}
        />
      )}
      
      {metadata && activeTab === 'reply' && (
        <div className="data-freshness" data-mobile={isMobile ? 'true' : 'false'}>
          <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center'}}>
            <span style={{fontSize: '12px'}}>
              {opportunities.length} opps
              {isMobile && <span className="pending-badge" style={{marginLeft: '6px'}}>🔵</span>}
            </span>
            <span style={{opacity: 0.4, fontSize: '11px'}}>·</span>
            <span style={{fontSize: '11px', opacity: 0.6}}>
              {opportunities.length === 0
                ? `Next batch ~${Math.floor(countdown / 60)}m ${countdown % 60}s`
                : 'Stable until cleared'}
            </span>
            <span style={{opacity: 0.4, fontSize: '11px'}}>·</span>
            <span style={{fontSize: '11px', opacity: 0.5}}>
              Last scan {metadata.lastScan
                ? metadata.lastScan.split('T')[1]?.slice(0, 5) + ' MT'
                : '—'}
            </span>
            {metadata.filterStats?.screen_coverage_today != null && (
              <>
                <span style={{opacity: 0.4, fontSize: '11px'}}>·</span>
                <span style={{fontSize: '11px', opacity: 0.6}}>
                  screened {metadata.filterStats.screen_coverage_today}% of eligible pool;
                  {' '}{metadata.filterStats.unscreened_in_current_batch || 0} unscreened in batch
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {activeTab === 'reply' && (
        <div className="sq-desk-section">
          <div className="quote-pacer-header">
            <span className="sq-desk-title">Shakespeare quote-posts</span>
            <span className="quote-pacer-hint">
              BitBard epigraph on a parent. Does not post. Mark Would ship or Veto — that is the evaluation.
              {sqStats
                ? ` · Ledger: ${sqStats.approved || 0} ship / ${sqStats.vetoed || 0} veto / ${sqStats.pending || 0} pending`
                : ''}
            </span>
          </div>
          {sqPackages.length === 0 && (
            <div className="quote-pacer-hint" style={{ marginTop: 8 }}>
              No packages today. Empty is valid — a thin morning ships nothing.
            </div>
          )}
          <div className="quote-pacer-cards">
            {sqPackages.map((pkg) => {
              const busy = sqActionId != null;
              const parse = pkg.parse_json || {};
              return (
                <div
                  key={pkg.id}
                  className={`quote-pacer-card${removingSq[pkg.id] ? ' slide-left' : ''}`}
                >
                  <div className="quote-pacer-card-top">
                    <span className={`quote-pacer-pool ${pkg.kind === 'sample' ? 'pool-sq-sample' : 'pool-sq-live'}`}>
                      {pkg.kind === 'sample' ? 'Sample' : 'Live'}
                    </span>
                    <span className="quote-pacer-char">{pkg.head_id}</span>
                    {pkg.mode && <span className="sq-desk-mode">{pkg.mode}</span>}
                  </div>
                  <div className="quote-pacer-target">
                    {pkg.post_url ? (
                      <a href={pkg.post_url} target="_blank" rel="noopener noreferrer" className="quote-pacer-link">
                        Open parent
                      </a>
                    ) : null}
                    {pkg.handle && <span className="quote-pacer-author">@{pkg.handle}</span>}
                  </div>
                  <div className="quote-pacer-snippet">{pkg.parent}</div>
                  <div className="sq-desk-epigraph">
                    <div className="sq-desk-cut">{pkg.deploy_cut}</div>
                    <div className="sq-desk-attr">{pkg.attribution}</div>
                  </div>
                  {pkg.why && <div className="sq-desk-why">Desk: {pkg.why}</div>}
                  {(parse.speaker || parse.stance) && (
                    <div className="sq-desk-parse">
                      {[parse.speaker, parse.stance, parse.object, parse.gravity]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  )}
                  <div className="quote-pacer-actions">
                    <button
                      type="button"
                      className="btn-quote-approve"
                      disabled={busy}
                      onClick={() => decideSqPackage(pkg, 'approved')}
                    >
                      {busy ? '…' : 'Would ship'}
                    </button>
                    <button
                      type="button"
                      className="btn-quote-dismiss"
                      disabled={busy}
                      onClick={() => decideSqPackage(pkg, 'vetoed')}
                    >
                      Veto
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === 'reply' && (
        <div className="quote-pacer-section">
          <div className="quote-pacer-header">
            <span className="quote-pacer-title">Quotes for followers</span>
            <span className="quote-pacer-hint">
              Prescreened liked or answered roots · Approve writes a new in-character quote (not a recycled reply)
              {quotePacerMeta?.proven?.under_floor?.length
                ? ` · Under weekly floor: ${quotePacerMeta.proven.under_floor.join(', ')}`
                : quotePacerMeta?.proven
                  ? ' · All characters at weekly cadence'
                  : ''}
            </span>
          </div>
          {quotePacerMeta?.error && (
            <div className="quote-pacer-error">Pacer error: {quotePacerMeta.error}</div>
          )}
          {proposedPosts.length === 0 && (
            <div className="quote-pacer-hint" style={{ marginTop: 8 }}>
              No eligible quote seeds right now (weekly/24h caps, author cooldown, or already quoted).
            </div>
          )}
            <div className="quote-pacer-cards">
              {proposedPosts.map((c) => {
                const cardKey = `${c.pool}-${c.opp_id}-${c.character}`;
                const busy = !!quoteActionKey;
                const cooledSet = new Set(quotePacerMeta?.proven?.cooled || []);
                const quotes7d = quotePacerMeta?.proven?.quotes_7d || {};
                return (
                  <div key={cardKey} className={`quote-pacer-card${removingQuotes[cardKey] ? ' slide-left' : ''}`}>
                    <div className="quote-pacer-card-top">
                      <span className="quote-pacer-pool pool-proven">Compose</span>
                      <span className="quote-pacer-char">{c.character}</span>
                      {c.source_reply_likes != null && (
                        <span className="quote-pacer-likes">
                          {c.source_reply_likes} like{c.source_reply_likes === 1 ? '' : 's'}
                          {c.source_reply_replies > 0
                            ? ` · ${c.source_reply_replies} ${c.source_reply_replies === 1 ? 'reply' : 'replies'}`
                            : ''}
                          {' on prior reply'}
                        </span>
                      )}
                    </div>
                    <div className="quote-pacer-target">
                      <a href={c.source_post_url} target="_blank" rel="noopener noreferrer" className="quote-pacer-link">
                        Open target post
                      </a>
                      <span className="quote-pacer-author">@{c.source_post_author}</span>
                    </div>
                    <div className="quote-pacer-snippet">
                      {(c.source_post_text || '').length > 300
                        ? c.source_post_text.slice(0, 300) + '…'
                        : c.source_post_text}
                    </div>
                    {c.source_reply_text && (
                      <div className="quote-pacer-reply-prior">
                        <span className="label">Prior hit (evidence, not caption):</span>{' '}
                        {c.source_reply_text.slice(0, 280)}
                        {c.source_reply_text.length > 280 ? '…' : ''}
                      </div>
                    )}
                    <div className="quote-pacer-char-select">
                      <label className="quote-char-label">Post as:</label>
                      <select
                        className="quote-char-dropdown"
                        disabled={busy}
                        value={quoteCharOverrides[cardKey] ?? c.character}
                        onChange={e => setQuoteCharOverrides(prev => ({ ...prev, [cardKey]: e.target.value }))}
                      >
                        {['Miranda', 'Falstaff', 'Lady Macbeth', 'Iago', 'Sir Andrew Aguecheek'].map(ch => {
                          const atCap = cooledSet.has(ch);
                          const n7 = quotes7d[ch] ?? 0;
                          return (
                            <option key={ch} value={ch} disabled={atCap && ch !== c.character}>
                              {ch}{ch === c.character ? ' ✦' : ''}{atCap ? ` (at weekly ${n7}/2)` : ` (${n7}/2 wk)`}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <div className="quote-pacer-actions">
                      <button
                        type="button"
                        className="btn-quote-approve"
                        disabled={busy}
                        onClick={() => approveQuoteCandidate(c)}
                      >
                        {busy ? '…' : 'Compose quote'}
                      </button>
                      <button
                        type="button"
                        className="btn-quote-dismiss"
                        disabled={busy}
                        onClick={() => dismissQuoteCandidate(c)}
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
        </div>
      )}

      {activeTab === 'reply' && (
        <>
      {!isMobile && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{opportunities.length}</div>
            <div className="stat-label">Pending</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {opportunities.filter(o => o.urgency === 'high').length}
            </div>
            <div className="stat-label">High Urgency (&lt;30 min)</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {opportunities.filter(o => o.age_minutes < 60).length}
            </div>
            <div className="stat-label">Fresh (&lt;1 hour)</div>
          </div>
          <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => fetchOpportunities({ force: true })}>
            <div className="stat-value" style={{ fontSize: '18px' }}>🔄</div>
            <div className="stat-label">Next Batch</div>
          </div>
        </div>
      )}

      {externalCandidates.length > 0 && (
        <div style={{
          margin: '0 0 16px 0',
          padding: '14px',
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          borderRadius: '12px',
          border: '1px solid #e2b04030'
        }}>
          <div style={{
            fontSize: '13px', fontWeight: 600, color: '#e2b040',
            marginBottom: '12px', letterSpacing: '0.5px'
          }}>
            EXTERNAL CONTENT ({externalCandidates.length})
            {extPostsRemaining != null && (
              <span style={{ marginLeft: '10px', fontSize: '11px', fontWeight: 400,
                color: extPostsRemaining === 0 ? '#f48fb1' : '#6e7681' }}>
                {extPostsRemaining === 0 ? 'Limit reached — resets in ≤12h' : `${extPostsRemaining} post${extPostsRemaining !== 1 ? 's' : ''} remaining`}
              </span>
            )}
          </div>
          {externalCandidates.map(ec => (
            <div key={ec.id} className={removingExternal[ec.id] ? 'slide-left' : ''} style={{
              background: '#0d1117', borderRadius: '10px', padding: '14px',
              marginBottom: '10px', border: '1px solid #30363d',
              transition: 'transform 0.35s ease-out, opacity 0.35s ease-out',
            }}>
              <div style={{
                background: '#161b22', borderRadius: '8px', padding: '10px 12px',
                border: '1px solid #21262d', marginBottom: '10px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <span style={{ color: '#58a6ff', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' }}>
                    {ec.topic_category}
                  </span>
                  <span style={{ color: '#6e7681', fontSize: '11px' }}>
                    {ec.publication}
                  </span>
                </div>
                <a href={ec.url} target="_blank" rel="noopener noreferrer"
                   style={{ color: '#e6edf3', fontSize: '14px', fontWeight: 600, lineHeight: 1.4, textDecoration: 'none' }}>
                  {ec.title}
                </a>
                {ec.lede && (
                  <div style={{ color: '#8b949e', fontSize: '12px', lineHeight: 1.5, marginTop: '6px' }}>
                    {ec.lede.slice(0, 160)}{ec.lede.length > 160 ? '...' : ''}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ color: '#c9d1d9', fontWeight: 600, fontSize: '13px' }}>
                    {charOverrideOpen[`ext-${ec.id}`] || ec.suggested_character}
                  </span>
                  <button
                    onClick={() => setCharOverrideOpen(prev => ({
                      ...prev, [`ext-${ec.id}`]: prev[`ext-${ec.id}`] ? null : ec.suggested_character
                    }))}
                    style={{
                      background: 'transparent', color: '#6e7681', border: '1px solid #30363d',
                      borderRadius: '10px', padding: '2px 8px', fontSize: '10px',
                      cursor: 'pointer'
                    }}
                  >
                    change
                  </button>
                </div>
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                  <button
                    onClick={() => approveExternalCandidate(ec)}
                    disabled={extPostsRemaining === 0}
                    style={{
                      background: extPostsRemaining === 0 ? '#30363d' : '#238636',
                      color: extPostsRemaining === 0 ? '#6e7681' : '#fff', border: 'none',
                      borderRadius: '6px', padding: '7px 16px', fontSize: '12px',
                      fontWeight: 600, cursor: extPostsRemaining === 0 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {extPostsRemaining === 0 ? 'Limit' : 'Post'}
                  </button>
                  <button
                    onClick={() => rejectExternalCandidate(ec)}
                    style={{
                      background: 'transparent', color: '#6e7681', border: '1px solid #30363d',
                      borderRadius: '6px', padding: '6px 10px', fontSize: '11px',
                      cursor: 'pointer'
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>
              {charOverrideOpen[`ext-${ec.id}`] && (
                <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
                  {['Miranda', 'Iago', 'Falstaff', 'Lady Macbeth', 'Sir Andrew Aguecheek'].map(c => (
                    <button key={c}
                      onClick={() => setCharOverrideOpen(prev => ({ ...prev, [`ext-${ec.id}`]: c }))}
                      style={{
                        background: charOverrideOpen[`ext-${ec.id}`] === c ? '#1f6feb' : '#21262d',
                        color: '#e6edf3', border: 'none', borderRadius: '12px',
                        padding: '4px 10px', fontSize: '11px', cursor: 'pointer'
                      }}
                    >
                      {c.replace('Sir Andrew Aguecheek', 'Sir Andrew')}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {opportunities.length === 0 && externalCandidates.length === 0 ? (
        <div className="empty-state">
          <h2>No opportunities right now</h2>
          <p>Page will refresh automatically when next scan completes</p>
        </div>
      ) : opportunities.length > 0 ? (
        <div ref={oppsListRef} className="opportunities-list" data-mobile={isMobile ? 'true' : 'false'}>
          {(() => {
            const n = opportunities.length;
            const lqCount = opportunities.filter(o => o.quality_tier === 'low').length;
            const agingCount = opportunities.filter(o => (o.age_minutes || 0) >= 60).length;
            const ages = opportunities.map(o => o.age_minutes || 0);
            const medianAge = n
              ? [...ages].sort((a, b) => a - b)[Math.floor((n - 1) / 2)]
              : 0;
            const lqShare = n > 0 ? lqCount / n : 0;
            const agingShare = n > 0 ? agingCount / n : 0;
            const thin = (metadata?.filterStats?.floor_rescue_count || 0) > 0;
            const riskHidden = metadata?.filterStats?.suppressed_risk || 0;
            const staleHidden = metadata?.filterStats?.suppressed_stale || 0;
            const maxAge = metadata?.filterStats?.slate_max_age_minutes || 90;
            // Combined stop cue: quality ↓ and/or age ↑ (operator session end signal).
            let stopHint = null;
            if (lqShare >= 0.5 && agingShare >= 0.5) {
              stopHint = `Stop — quality down & age up: ${lqCount}/${n} LQ, ${agingCount}/${n} ≥60m (median ${medianAge}m). End the session.`;
            } else if (lqShare >= 0.5) {
              stopHint = `Stop — quality down: ${lqCount}/${n} LQ. Skip freely or end the session.`;
            } else if (agingShare >= 0.5 || medianAge >= 60) {
              stopHint = `Stop — age not optimal: ${agingCount}/${n} ≥60m (median ${medianAge}m). Prefer ending over forcing replies.`;
            } else if (lqShare >= 0.25 || agingShare >= 0.25) {
              stopHint = `Watch — ${lqCount}/${n} LQ · ${agingCount}/${n} ≥60m · median ${medianAge}m. Be picky.`;
            }
            // Always show — this is the operator's "should I keep going?" meter.
            return (
              <div style={{
                fontSize: '13px', marginBottom: '12px',
                padding: '10px 12px', borderRadius: '6px',
                border: stopHint && stopHint.startsWith('Stop') ? '1px solid #c62828'
                  : stopHint ? '1px solid #ef6c00' : '1px solid #555',
                background: stopHint && stopHint.startsWith('Stop')
                  ? 'rgba(198,40,40,0.12)'
                  : stopHint ? 'rgba(239,108,0,0.10)' : 'rgba(0,0,0,0.25)',
                color: '#e6edf3',
              }}>
                {stopHint
                  ? <div style={{ fontWeight: 600, marginBottom: 4 }}>{stopHint}</div>
                  : (
                    <div style={{ fontWeight: 500, marginBottom: 4, opacity: 0.95 }}>
                      Session OK: {lqCount}/{n} LQ · median age {medianAge}m · {agingCount}/{n} ≥60m
                    </div>
                  )}
                {thin && (
                  <div style={{ opacity: 0.85, marginBottom: 4 }}>
                    Thin pool — best available below the usual quality bar.
                  </div>
                )}
                {(riskHidden > 0 || staleHidden > 0) && (
                  <div style={{ opacity: 0.7, fontSize: '12px' }}>
                    Hid{[
                      riskHidden > 0 ? ` ${riskHidden} caution/danger` : null,
                      staleHidden > 0 ? ` ${staleHidden} ≥${maxAge}m` : null,
                    ].filter(Boolean).join(' ·')}
                    {' '}(auto-suppressed).
                  </div>
                )}
                <div style={{ opacity: 0.55, fontSize: '11px', marginTop: 6 }}>
                  Keep going while LQ is low and median age &lt;60m. Slate hides ≥{maxAge}m.
                </div>
              </div>
            );
          })()}
          {opportunities.map(opp => (
            <div 
              key={opp.id} 
              className={`opportunity ${opp.urgency} ${removingOpps[opp.id] ? `slide-${removingOpps[opp.id]}` : ''}`}
              id={`opp-${opp.id}`}
            >
              <div className="opp-header">
                <div className="score-container">
                  {opp.quality_tier === 'low' && (
                    <div style={{
                      background: '#b71c1c', color: '#fff', padding: '2px 8px',
                      borderRadius: '8px', fontSize: '11px', fontWeight: 700,
                      letterSpacing: '0.4px',
                    }} title="Low-quality input: pile-on/verdict/thin — prefer Skip">
                      LQ — weak surface
                    </div>
                  )}
                  {(opp.age_minutes || 0) >= 60 && (
                    <div style={{
                      background: '#e65100', color: '#fff', padding: '2px 8px',
                      borderRadius: '8px', fontSize: '11px', fontWeight: 700,
                    }} title="Past the sweet spot (~under 60m); hit rate drops">
                      AGING
                    </div>
                  )}
                  {opp.below_score_floor_rescue && (
                    <div style={{
                      background: '#5d4037', color: '#fff', padding: '1px 6px',
                      borderRadius: '8px', fontSize: '10px', fontWeight: 600
                    }} title="Best available while the quality floor emptied the batch">
                      THIN POOL
                    </div>
                  )}
                  {!opp.is_substantive && (
                    <div className="warning-badge" title="Low-content post - may not merit reply">
                      ⚠️ Low-content
                    </div>
                  )}
                </div>
                <div className="opp-meta">
                    <div className="author">
                      {opp.avatar_url && (
                        <img
                          src={opp.avatar_url}
                          alt={opp.author_display || opp.author}
                          className="opp-avatar"
                        />
                      )}
                      <span>
                        {opp.author_display ? `${opp.author_display} ` : ''}@{opp.author}
                      </span>
                    </div>
                  <div className="age">
                    {opp.followers.toLocaleString()} followers · 
                    <span style={{
                      color: opp.age_minutes < 30 ? '#4caf50' :
                             opp.age_minutes < 60 ? '#cddc39' :
                             opp.age_minutes < 90 ? '#ff9800' : '#f44336',
                      fontWeight: opp.age_minutes < 30 ? 600 : 400
                    }}>
                      Posted {opp.age_minutes}m ago
                    </span>
                  </div>
                </div>
              </div>

              {/* User's reply text */}
              <div className="post-text">"{opp.text}"</div>

              {/* Display embedded images from the tracked account's post */}
              {opp.embed?.images && opp.embed.images.length > 0 && (
                <div className="post-images" style={{
                  display: 'grid',
                  gridTemplateColumns: opp.embed.images.length === 1 ? '1fr' : 'repeat(2, 1fr)',
                  gap: '8px',
                  margin: '12px 0',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  {opp.embed.images.map((img, i) => (
                    <img 
                      key={i}
                      src={img.thumb || img.fullsize}
                      alt=""
                      style={{
                        width: '100%',
                        height: opp.embed.images.length === 1 ? '300px' : '200px',
                        objectFit: 'cover',
                        cursor: 'pointer',
                        borderRadius: '4px'
                      }}
                      onClick={() => window.open(opp.post_url, '_blank')}
                    />
                  ))}
                </div>
              )}

              {/* Display external link embeds */}
              {opp.embed?.external && (
                <div className="external-embed" style={{
                  border: '1px solid #333',
                  borderRadius: '8px',
                  padding: '12px',
                  margin: '12px 0',
                  background: '#1a1a1a',
                  cursor: 'pointer'
                }}
                onClick={() => window.open(opp.embed.external.uri, '_blank')}
                >
                  {opp.embed.external.thumb && (
                    <img 
                      src={opp.embed.external.thumb}
                      alt=""
                      style={{
                        width: '100%',
                        maxHeight: '200px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        marginBottom: '8px'
                      }}
                    />
                  )}
                  <div style={{fontSize: '13px', fontWeight: 600, marginBottom: '4px'}}>
                    {opp.embed.external.title}
                  </div>
                  {opp.embed.external.description && (
                    <div style={{fontSize: '12px', opacity: 0.8}}>
                      {opp.embed.external.description}
                    </div>
                  )}
                </div>
              )}

              {/* Embedded "Replying to" / "Quoting" card - Bluesky style */}
              {opp.parent_uri && threadPosts[opp.parent_uri] && (
                <div className="reply-to-card">
                  <div className="reply-to-header">
                    {opp.parent_uri === opp.root_uri ? (
                      <>💬 Quoting @{threadPosts[opp.parent_uri]?.author?.handle || 'unknown'}</>
                    ) : (
                      <>↩️ Replying to @{threadPosts[opp.parent_uri]?.author?.handle || 'unknown'}</>
                    )}
                  </div>
                  <BlueskyPostCard 
                    post={threadPosts[opp.parent_uri]} 
                    level="embedded"
                    compact={true}
                  />
                </div>
              )}

              {/* Show root context if different from parent (thread chain) */}
              {opp.root_uri && opp.root_uri !== opp.parent_uri && threadPosts[opp.root_uri] && (
                <div className="thread-origin">
                  <div className="thread-origin-header">
                    🧵 Thread started by @{threadPosts[opp.root_uri]?.author?.handle || 'unknown'}
                  </div>
                  <BlueskyPostCard 
                    post={threadPosts[opp.root_uri]} 
                    level="root"
                    compact={true}
                  />
                </div>
              )}

              {/* Fallback: No rich context available but we have text */}
              {!opp.parent_uri && !opp.root_uri && opp.parent_text && (
                <div className="reply-to-card reply-to-text-only">
                  <div className="reply-to-header">↩️ Replying to:</div>
                  <div className="reply-to-text">"{opp.parent_text}"</div>
                </div>
              )}
              
              {!opp.is_substantive && (
                <div className="low-content-flag">
                  💬 Low-content post - may not merit agent reply
                </div>
              )}

              {/* ── Action strip: Post primary, Skip secondary (caution hidden upstream) ── */}
              {(() => {
                const charRaw = String(opp.character || '');
                const charKey = charRaw.toLowerCase();
                const charMapping = CHARACTER_KEYS[charKey];
                let charAvatar = null;
                if (charMapping && charAvatars) {
                  for (const m of charMapping) {
                    if (charAvatars[m]) { charAvatar = charAvatars[m]; break; }
                  }
                }
                const CHAR_SHORTCODES = {
                  'falstaff': 'F', 'lady macbeth': 'LM', 'miranda': 'M',
                  'iago': 'I', 'sir andrew aguecheek': 'Andrew', 'sir andrew': 'Andrew',
                };
                const shortcode = CHAR_SHORTCODES[charKey] || charRaw;
                const overrideOpen = charOverrideOpen[opp.id] || false;

                return (
                  <div className="action-strip">
                    {opp.cooldown_flag && (
                      <div className="action-strip-warning">
                        Cooldown (2x in 48h)
                      </div>
                    )}

                    {overrideOpen && (
                      <div className="char-override-row">
                        {[
                          {code: 'F', label: 'F', title: 'Falstaff'},
                          {code: 'LM', label: 'LM', title: 'Lady Macbeth'},
                          {code: 'M', label: 'M', title: 'Miranda'},
                          {code: 'I', label: 'I', title: 'Iago'},
                          {code: 'Andrew', label: 'SA', title: 'Sir Andrew'},
                        ].map(c => (
                          <button
                            key={c.code}
                            className={`btn-char-pill${c.code === shortcode ? ' active' : ''}`}
                            title={c.title}
                            disabled={opp.cooldown_flag}
                            onClick={() => {
                              setCharOverrideOpen(prev => ({...prev, [opp.id]: false}));
                              postReplyToDiscord(c.code, opp.post_url, opp.id);
                            }}
                          >
                            {c.label}
                          </button>
                        ))}
                        <button
                          className="btn-char-pill cancel"
                          onClick={() => setCharOverrideOpen(prev => ({...prev, [opp.id]: false}))}
                        >
                          ✕
                        </button>
                      </div>
                    )}

                    {!overrideOpen && (
                      <button
                        className="btn-change-char"
                        onClick={() => setCharOverrideOpen(prev => ({...prev, [opp.id]: true}))}
                      >
                        change character
                      </button>
                    )}

                    <div className="action-buttons">
                      <button
                        className="btn-skip-secondary"
                        onClick={() => skipOpportunityWithReason(opp.id, 'skipped')}
                      >
                        Skip
                      </button>
                      <button
                        className="btn-post-primary"
                        onClick={() => postReplyToDiscord(shortcode, opp.post_url, opp.id)}
                        disabled={opp.cooldown_flag}
                      >
                        {charAvatar && <img src={charAvatar} alt="" className="btn-char-avatar" />}
                        Post as {charRaw}
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      ) : null}
        </>
      )}

      {activeTab === 'spotlight' && (
        <SpotlightTab showToast={showToast} />
      )}

      {activeTab === 'health' && (
        <HealthTab apiUrl={BACKEND_URL} poolSummary={poolSummary} />
      )}

      {activeTab === 'dramaturg' && (
        <DramaturgWorkshop showToast={showToast} />
      )}
      
      
      {toast && (
        <div className="toast-notification">
          {toast}
          </div>
      )}
    </div>
  );
};

export default Opportunities;

